import { useCallback, useState } from 'react';
import { Element, Form, Tag } from 'react-bulma-components';
import FeedbackMessage from '../feedback-message/feedback-message';

type BulmaFormInput = React.ComponentProps<typeof Form.Input>;

const keys = ['Enter', ' ', ','];

export type TagListInputProps = BulmaFormInput & {
  elements: string[];
  onChange: (elements: string[]) => void;
  validate?: (value: string) => boolean;
  errorText?: string;
};

export function TagListInput({
  elements,
  onChange,
  validate = () => true,
  errorText,
  ...inputProps
}: TagListInputProps) {
  const [input, setInput] = useState('');
  const [showError, setShowError] = useState(false);

  const handleInputChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      setInput(evt.target.value);
      if (evt.target.value === '') {
        setShowError(false);
      }
    },
    []
  );

  const handleOnChange = useCallback(() => {
    if (input.trim() === '') return;

    const values = input
      .split(',')
      .map((values) => values.trim())
      .filter((value) => value !== '');
    const isValid = values.every((value) => validate(value));

    if (isValid) {
      const newValues = new Set(
        values.filter((value) => !elements.includes(value))
      );

      onChange([...elements, ...newValues]);

      setInput('');
      setShowError(false);
    } else {
      setShowError(true);
    }
  }, [elements, input, onChange, validate]);

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> =
    useCallback(
      (evt) => {
        if (keys.includes(evt.key)) {
          evt.preventDefault();

          handleOnChange();
        }
      },
      [handleOnChange]
    );

  const handleElementRemove = useCallback(
    (index: number) => {
      const newElements = [...elements];
      newElements.splice(index, 1);
      onChange(newElements);
    },
    [elements, onChange]
  );

  const handleOnBlur = useCallback(() => {
    handleOnChange();
  }, [handleOnChange]);

  return (
    <>
      <Form.Control>
        <Form.Input
          className="placeholder-text-gray"
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleOnBlur}
          {...inputProps}
        />
      </Form.Control>

      {showError && errorText && <FeedbackMessage>{errorText}</FeedbackMessage>}

      <Element mt={2} display="flex" alignItems="center" flexWrap="wrap">
        {elements.map((element, index) => (
          <Tag.Group
            hasAddons
            key={`${index}-${element}`}
            className={`mr-2 ${index === elements.length - 1 ? 'mb-4' : ''}`}
          >
            <Tag
              rounded
              remove
              className={'is-primary is-light'}
              colorVariant={'light'}
              onClick={() => handleElementRemove(index)}
            />
            <Tag
              rounded
              className={'is-primary is-light'}
              colorVariant={'light'}
            >
              {element}
            </Tag>
          </Tag.Group>
        ))}
      </Element>
    </>
  );
}

export default TagListInput;
